import type { MemberCard, RegistrationStatus } from '@ipc/shared'
import { Navigate, useParams } from 'react-router'
import { isApiError } from '~/api/client'
import { useJoinInfo, useMe, useRegistration } from '~/api/hooks'
import { Outcome } from '~/biometrics/Outcome'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { MemberLayout } from '~/components/member/layout'
import { StepSkeleton } from '~/components/member/StepSkeleton'
import { claimEnded, claimRefused, REGISTRATION_ENDED_STATE, useAutoClaim, useSessionClaim } from '~/lib/claim'
import { nextForStatus, pathForStatus } from '~/lib/flow'
import { StatusCard } from './StatusCard'

/**
 * The card of a registration that stays on this page (`nextForStatus` = `status`: rejected or erased, or an approved
 * or review registration whose claim was refused). A failed try (`retry_allowed`) goes back to the face step and never
 * gets the "a person asked for a new face check" card.
 */
function cardOf(r: RegistrationStatus): MemberCard {
  switch (r.status) {
    case 'approved':
      return 'approved'
    case 'review':
      return 'review'
    default:
      return 'not_accepted'
  }
}

/**
 * S6 (`/join/:providerId/status`). An approved or review registration (`claimable`) becomes the voter session by
 * itself (passwordless spec §2, no button) and the page goes on to the voter page; a 409 keeps the result here, a 401
 * (the registration ended on this device) goes to the sign-in page, which offers the sign-in email. Any other result
 * (not accepted, erased) is shown here in plain words. Without a registration cookie, a signed-in voter goes to the
 * voter page and anyone else to the sign-in page.
 */
export default function JoinStatus() {
  const { providerId = '' } = useParams()
  const friendly = useFriendlyError()
  const reg = useRegistration()
  const unauth = reg.isError && isApiError(reg.error) && reg.error.status === 401
  const me = useMe(unauth)
  const join = useJoinInfo(reg.data?.provider ?? providerId)
  const claim = useSessionClaim()
  const claimable = reg.data?.claimable === true && nextForStatus(reg.data) === 'status'
  useAutoClaim(claimable, claim)

  const skeleton = (
    <MemberLayout>
      <StepSkeleton />
    </MemberLayout>
  )
  if (reg.isPending || (unauth && me.isPending)) return skeleton

  if (reg.data) {
    const r = reg.data
    if (r.status === 'blocked_duplicate')
      return (
        <MemberLayout>
          <Outcome kind='duplicate' />
        </MemberLayout>
      )
    if (nextForStatus(r) !== 'status') return <Navigate to={pathForStatus(r)} replace />
    if (claimable && !claimRefused(claim)) {
      if (claimEnded(claim)) return <Navigate to='/login' replace state={REGISTRATION_ENDED_STATE} />
      if (claim.isError)
        return (
          <MemberLayout>
            <RetryError error={claim.error} onRetry={() => claim.mutate()} />
          </MemberLayout>
        )
      // Claiming, or on the way to the voter page.
      return skeleton
    }
    return (
      <MemberLayout>
        <StatusCard
          card={cardOf(r)}
          name={r.name}
          reasonCategories={r.reasonCategories}
          providerKeepsPhotos={join.data?.faceNotice === 'kept_for_review'}
          providerName={r.providerDisplayName}
          retryTo={`/join/${r.provider}/face`}
        />
      </MemberLayout>
    )
  }

  // No registration cookie (it ends with the claim): the voter page has the card.
  if (unauth && me.data) return <Navigate to='/me' replace />
  if (unauth) return <Navigate to='/login' replace state={REGISTRATION_ENDED_STATE} />
  return (
    <MemberLayout>
      <ErrorAlert>{friendly(reg.error)}</ErrorAlert>
    </MemberLayout>
  )
}
